/**
 * Complete Production-Grade Shipping, Courier Integration & Logistics Route — Module 8.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { siteConfig } from "@/config/site";
import {
  Truck,
  PackageCheck,
  MapPin,
  Clock,
  ShieldCheck,
  CheckCircle2,
  Activity,
  Plus,
  Save,
  Check,
  Settings,
  AlertCircle,
} from "lucide-react";
import { DataTable, type ColumnDef } from "@/components/admin/common/data-table";
import {
  fetchShippingZonesFromSupabase,
  fetchCouriersFromSupabase,
  fetchShipmentsFromSupabase,
  getStoredShippingZones,
  getStoredCouriers,
  getStoredShipments,
  type CourierPartner,
  type ShippingZone,
  type ShipmentRecord,
} from "@/features/admin/shipping/store/admin-shipping-store";
import {
  useStoreShippingSettings,
  saveShippingSettingsToSupabase,
} from "@/lib/store-settings";

export const Route = createFileRoute("/admin/shipping")({
  head: () => ({
    meta: [{ title: `Logistics & Shipping — Admin — ${siteConfig.name}` }],
  }),
  component: AdminShippingPage,
});

function AdminShippingPage() {
  const globalShipping = useStoreShippingSettings();

  const [couriers, setCouriers] = useState<CourierPartner[]>(() => getStoredCouriers());
  const [zones, setZones] = useState<ShippingZone[]>(() => getStoredShippingZones());
  const [shipments, setShipments] = useState<ShipmentRecord[]>(() => getStoredShipments());

  // Editable storewide shipping charges
  const [standardRate, setStandardRate] = useState<number | "">(
    globalShipping.standardShippingFee,
  );
  const [expressRate, setExpressRate] = useState<number | "">(
    globalShipping.expressShippingFee,
  );
  const [freeThreshold, setFreeThreshold] = useState<number | "">(
    globalShipping.freeShippingThreshold,
  );
  const [standardDays, setStandardDays] = useState<string>(globalShipping.standardDays);
  const [expressDays, setExpressDays] = useState<string>(globalShipping.expressDays);

  const [ratesSaved, setRatesSaved] = useState(false);
  const [savingError, setSavingError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Sync state if global shipping updates from another tab/page
  useEffect(() => {
    setStandardRate(globalShipping.standardShippingFee);
    setExpressRate(globalShipping.expressShippingFee);
    setFreeThreshold(globalShipping.freeShippingThreshold);
    setStandardDays(globalShipping.standardDays);
    setExpressDays(globalShipping.expressDays);
  }, [globalShipping]);

  useEffect(() => {
    Promise.all([
      fetchCouriersFromSupabase(),
      fetchShippingZonesFromSupabase(),
      fetchShipmentsFromSupabase(),
    ]).then(([couriersData, zonesData, shipmentsData]) => {
      if (couriersData && couriersData.length > 0) setCouriers(couriersData);
      if (zonesData && zonesData.length > 0) setZones(zonesData);
      if (shipmentsData && shipmentsData.length > 0) setShipments(shipmentsData);
    });
  }, []);

  const handleSaveRates = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSavingError(null);

    const parsedStandard = standardRate === "" ? 0 : Math.max(0, Number(standardRate) || 0);
    const parsedExpress = expressRate === "" ? 0 : Math.max(0, Number(expressRate) || 0);
    const parsedThreshold = freeThreshold === "" ? 0 : Math.max(0, Number(freeThreshold) || 0);

    try {
      await saveShippingSettingsToSupabase({
        standardShippingFee: parsedStandard,
        expressShippingFee: parsedExpress,
        freeShippingThreshold: parsedThreshold,
        standardDays: standardDays.trim() || "5–7",
        expressDays: expressDays.trim() || "1–2",
      });

      setRatesSaved(true);
      setTimeout(() => setRatesSaved(false), 3000);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || "Failed to save shipping rates";
      setSavingError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // Summary Metrics
  const stats = useMemo(() => {
    const inTransit = shipments.filter(
      (s) => s.status === "IN_TRANSIT" || s.status === "OUT_FOR_DELIVERY",
    ).length;
    const delivered = shipments.filter((s) => s.status === "DELIVERED").length;
    const totalPincodes = zones.reduce((acc, z) => acc + z.pincodesCount, 0);

    return { inTransit, delivered, totalPincodes };
  }, [shipments, zones]);

  // DataTable Columns
  const columns: ColumnDef<ShipmentRecord>[] = [
    {
      id: "awbNumber",
      header: "AWB Tracking Code",
      accessorKey: "awbNumber",
      cell: (row) => (
        <div>
          <span className="font-mono font-bold text-foreground text-xs block">{row.awbNumber}</span>
          <span className="text-[10px] text-muted-foreground font-mono">{row.courierName}</span>
        </div>
      ),
      sortable: true,
    },
    {
      id: "orderId",
      header: "Order Ref",
      accessorKey: "orderId",
      cell: (row) => <span className="font-mono text-xs font-semibold">{row.orderId}</span>,
      sortable: true,
    },
    {
      id: "customerName",
      header: "Destination Customer",
      accessorKey: "customerName",
      cell: (row) => (
        <div>
          <p className="font-semibold text-foreground text-xs">{row.customerName}</p>
          <p className="text-[11px] text-muted-foreground font-mono">
            {row.city} ({row.pincode})
          </p>
        </div>
      ),
      sortable: true,
    },
    {
      id: "weightKg",
      header: "Parcel Weight & Size",
      accessorKey: "weightKg",
      cell: (row) => (
        <div>
          <span className="font-mono text-xs font-semibold block">{row.weightKg} kg</span>
          <span className="text-[10px] text-muted-foreground font-mono">{row.dimensionsCm}</span>
        </div>
      ),
    },
    {
      id: "status",
      header: "Logistics Status",
      accessorKey: "status",
      cell: (row) => {
        const colors: Record<string, string> = {
          DELIVERED: "bg-emerald-100 text-emerald-800",
          OUT_FOR_DELIVERY: "bg-purple-100 text-purple-800",
          IN_TRANSIT: "bg-blue-100 text-blue-800",
          MANIFESTED: "bg-amber-100 text-amber-800",
          RTO_RETURNED: "bg-rose-100 text-rose-800",
        };
        return (
          <span
            className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
              colors[row.status] || "bg-secondary text-foreground"
            }`}
          >
            {row.status}
          </span>
        );
      },
      sortable: true,
    },
    {
      id: "estimatedDeliveryDate",
      header: "Estimated Delivery",
      accessorKey: "estimatedDeliveryDate",
      cell: (row) => (
        <span className="font-mono text-xs text-muted-foreground">{row.estimatedDeliveryDate}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-16">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-semibold">
            Logistics & Courier Architecture
          </p>
          <h1 className="mt-1 font-display text-3xl text-foreground">
            Shipping & Fulfillment Control
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Manage store shipping charges, courier API integrations, rate matrix, and real-time dispatch tracking.
          </p>
        </div>
        <div>
          <Link
            to="/admin/settings"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border px-3 py-2 rounded-md bg-card shadow-2xs hover:bg-secondary/40 transition-colors"
          >
            <Settings className="h-3.5 w-3.5" />
            <span>Store Configuration</span>
          </Link>
        </div>
      </div>

      {/* Global Shipping Rates Management Box */}
      <div className="bg-card border border-border rounded-lg p-5 shadow-2xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold text-foreground">
              Storewide Shipping Charges & Delivery Rules
            </h2>
          </div>
          <span className="text-[10px] font-mono uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold self-start sm:self-auto">
            Active in Cart & Checkout
          </span>
        </div>

        {ratesSaved && (
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 p-3 rounded border border-emerald-200 shadow-xs animate-in fade-in duration-150">
            <Check className="h-4 w-4 text-emerald-600 shrink-0" />
            <span>Shipping rates updated successfully! New rates are now active across the store.</span>
          </div>
        )}

        {savingError && (
          <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 p-3 rounded border border-rose-200 shadow-xs animate-in fade-in duration-150">
            <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
            <span>Error updating rates: {savingError}</span>
          </div>
        )}

        <form onSubmit={handleSaveRates} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="eyebrow text-[11px] block mb-1 font-semibold">
                Standard Shipping Fee (₹)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={standardRate}
                onChange={(e) =>
                  setStandardRate(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full min-h-10 border border-border bg-background px-3 text-xs font-semibold tabular-nums rounded focus:border-primary outline-none"
                placeholder="60"
              />
              <span className="text-[10px] text-muted-foreground block mt-1">
                Applied on standard deliveries when below free shipping minimum.
              </span>
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1 font-semibold">
                Express Shipping Fee (₹)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={expressRate}
                onChange={(e) =>
                  setExpressRate(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full min-h-10 border border-border bg-background px-3 text-xs font-semibold tabular-nums rounded focus:border-primary outline-none"
                placeholder="699"
              />
              <span className="text-[10px] text-muted-foreground block mt-1">
                Charged on priority expedited deliveries.
              </span>
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1 font-semibold">
                Free Shipping Threshold (₹)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={freeThreshold}
                onChange={(e) =>
                  setFreeThreshold(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full min-h-10 border border-border bg-background px-3 text-xs font-semibold tabular-nums rounded focus:border-primary outline-none"
                placeholder="5000"
              />
              <span className="text-[10px] text-muted-foreground block mt-1">
                Orders with cart subtotal ≥ this amount get free standard delivery.
              </span>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 pt-1 border-t border-border/50">
            <div>
              <label className="eyebrow text-[11px] block mb-1">
                Standard Transit SLA (Days)
              </label>
              <input
                type="text"
                value={standardDays}
                onChange={(e) => setStandardDays(e.target.value)}
                className="w-full min-h-9 border border-border bg-background px-3 text-xs rounded focus:border-primary outline-none"
                placeholder="5–7"
              />
            </div>
            <div>
              <label className="eyebrow text-[11px] block mb-1">
                Express Transit SLA (Days)
              </label>
              <input
                type="text"
                value={expressDays}
                onChange={(e) => setExpressDays(e.target.value)}
                className="w-full min-h-9 border border-border bg-background px-3 text-xs rounded focus:border-primary outline-none"
                placeholder="1–2"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground font-mono">
              Live Rate: Standard ₹{standardRate === "" ? 0 : standardRate} • Express ₹{expressRate === "" ? 0 : expressRate} • Free over ₹{freeThreshold === "" ? 0 : freeThreshold}
            </span>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-xs uppercase tracking-wider font-semibold text-primary-foreground rounded hover:opacity-90 transition-opacity shadow-xs disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" />
              <span>{isSaving ? "Saving..." : "Update Shipping Rates"}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Summary Metric Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            In-Transit Active Shipments
          </span>
          <span className="font-display text-xl font-bold text-blue-700">
            {stats.inTransit} Parcels
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Successfully Delivered
          </span>
          <span className="font-display text-xl font-bold text-emerald-700">
            {stats.delivered} Parcels
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Serviceable Pincodes Coverage
          </span>
          <span className="font-display text-xl font-bold text-foreground">
            {stats.totalPincodes.toLocaleString("en-IN")} Pincodes
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Standard Delivery Fee
          </span>
          <span className="font-display text-xl font-bold text-primary">₹{standardRate}</span>
        </div>
      </div>

      {/* Courier API Integration Cards Grid */}
      <div className="space-y-3">
        <p className="eyebrow text-xs text-foreground font-semibold">
          Integrated Courier API Partners
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          {couriers.map((c) => (
            <div
              key={c.id}
              className="p-4 rounded-lg bg-card border border-border space-y-2 shadow-2xs"
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold text-sm text-foreground">{c.name}</span>
                <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                  API LIVE
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Services: {c.serviceTypes.join(" • ")}
              </p>
              <div className="pt-2 border-t border-border/60 flex justify-between text-[10px] font-mono text-muted-foreground">
                <span>Active Shipments:</span>
                <strong className="text-foreground">{c.activeShipmentsCount}</strong>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Shipping Zones Overview */}
      <div className="space-y-3 pt-2 border-t">
        <p className="eyebrow text-xs text-foreground font-semibold">
          Configured Shipping Zones & Rate Matrix
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {zones.map((z) => (
            <div
              key={z.id}
              className="p-4 rounded-lg bg-secondary/30 border border-border space-y-1.5 text-xs"
            >
              <p className="font-semibold text-foreground">{z.name}</p>
              <div className="grid grid-cols-3 gap-2 font-mono text-[11px] text-muted-foreground pt-1">
                <div>
                  <span className="block text-[9px] uppercase">Base Shipping Rate</span>
                  <strong className="text-foreground">₹{standardRate}</strong>
                </div>
                <div>
                  <span className="block text-[9px] uppercase">Free Shipping Above</span>
                  <strong className="text-emerald-700">₹{freeThreshold}</strong>
                </div>
                <div>
                  <span className="block text-[9px] uppercase">Delivery Estimate</span>
                  <strong className="text-foreground">{z.deliveryEstimateDays || standardDays}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Shipments DataTable */}
      <div className="space-y-3 pt-2 border-t">
        <p className="eyebrow text-xs text-foreground font-semibold">
          Live Dispatch Shipment Telemetry
        </p>
        <DataTable
          data={shipments}
          columns={columns}
          keyExtractor={(row) => row.id}
          searchPlaceholder="Search by AWB tracking code, order ID, or customer..."
          searchField={(row) => `${row.awbNumber} ${row.orderId} ${row.customerName} ${row.city}`}
        />
      </div>
    </div>
  );
}
